// Modal de envio para assinatura digital — compartilhado (2026-08-21).
//
// Nasceu em `pages/administrativo-expedientes/ModalAssinatura.tsx` e foi
// generalizado quando as VENDAS passaram a assinar pelo mesmo motor. O que
// muda por módulo (quem já vem pré-selecionado, o texto do aviso, e qual
// action chamar) entra por props; o resto — montar a lista, qualificação,
// ordem de assinatura, recado no e-mail — é igual para todo mundo.
//
// Interno = pessoa do sistema (escolhida numa lista, e-mail não é digitável).
// Externo = alguém de fora, com nome e e-mail digitados à mão.

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, App as AntApp, AutoComplete, Button, Checkbox, Flex, Input, Modal, Select,
  Space, Table, Tag, Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { SignatarioEnvio, QUALIFICACOES, papeisFaltando } from './tipos';

const { Text, Paragraph } = Typography;

type Usuario = { email: string; nome?: string };

export interface PropsModalAssinatura {
  open: boolean;
  /** Nome do documento no título ("OF-2026-001", "VD-2026-014"). */
  titulo: string | null;
  /** Quem já entra na lista ao abrir. O usuário edita à vontade antes de enviar. */
  preSelecionados: SignatarioEnvio[];
  /** Uma linha explicando QUAL arquivo vai ser assinado. */
  descricaoDoAviso: string;
  /**
   * Papéis que o documento EXIGE em "Assina como" (venda: Contratante e
   * Contratada). Enquanto faltar um, o botão de enviar fica desligado e a
   * janela diz qual é — a mesma recusa que o servidor dá. `[]` = sem exigência
   * (expediente/ofício, cuja trava ainda não existe no servidor).
   *
   * OBRIGATÓRIA de propósito: como opcional, apagar a linha do chamador da
   * venda compilava e o CI ficava verde sem a exigência (revisão adversarial de
   * 15/09/2026). Agora o `npm run build` do CI reprova quem esquecer.
   */
  papeisObrigatorios: string[];
  /** Faz a chamada da action do módulo. Devolve quantos signatários entraram. */
  onEnviar: (p: { signatarios: SignatarioEnvio[]; workflow: boolean; mensagem?: string })
    => Promise<{ signatarios: number }>;
  onClose: () => void;
  onEnviado: () => void;
}

export default function ModalAssinaturaDigital({
  open, titulo, preSelecionados, descricaoDoAviso, papeisObrigatorios, onEnviar, onClose, onEnviado,
}: PropsModalAssinatura) {
  const { message } = AntApp.useApp();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [lista, setLista] = useState<SignatarioEnvio[]>([]);
  const [workflow, setWorkflow] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Rascunho do signatário sendo adicionado
  const [tipo, setTipo] = useState<'interno' | 'externo'>('interno');
  const [interno, setInterno] = useState<string | undefined>();
  const [extNome, setExtNome] = useState('');
  const [extEmail, setExtEmail] = useState('');
  const [qualif, setQualif] = useState('');

  // 🚨 A sugestão do CONTRATO chega por HTTP depois de a janela abrir (o
  // Jurídico busca as partes pela ficha). O efeito de abertura roda uma vez, com
  // deps [open]: quando a resposta chegava, a lista já estava com os envolvidos
  // internos, sem "Assina como" — e com a exigência das partes o botão ficaria
  // desligado para sempre (revisão adversarial de 15/09/2026). Um efeito
  // separado adota a sugestão nova ENQUANTO a pessoa não mexeu na lista; depois
  // que ela mexe, nada sobrescreve o que ela montou.
  const tocado = useRef(false);
  useEffect(() => {
    if (!open || tocado.current) return;
    setLista(preSelecionados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preSelecionados]);

  useEffect(() => {
    if (!open) return;
    tocado.current = false;
    setLista(preSelecionados); setWorkflow(false); setMensagem(''); setQualif('');
    setTipo('interno'); setInterno(undefined); setExtNome(''); setExtEmail('');
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios(Array.isArray(d) ? d : []);
      } catch { setUsuarios([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function adicionar() {
    if (tipo === 'interno') {
      if (!interno) { message.warning('Escolha a pessoa'); return; }
      const u = usuarios.find(x => x.email === interno);
      if (lista.some(s => s.email.toLowerCase() === interno.toLowerCase())) {
        message.warning('Essa pessoa já está na lista'); return;
      }
      setLista(l => [...l, {
        nome: u?.nome || interno.split('@')[0], email: interno,
        origem: 'interno', usuario_email: interno, ordem: l.length + 1, d4sign_act: '1',
        qualificacao: qualif.trim() || undefined,
      }]);
      setInterno(undefined); setQualif('');
    } else {
      const email = extEmail.trim().toLowerCase();
      if (!extNome.trim() || !email) { message.warning('Preencha nome e e-mail'); return; }
      if (lista.some(s => s.email.toLowerCase() === email)) {
        message.warning('Esse e-mail já está na lista'); return;
      }
      setLista(l => [...l, {
        nome: extNome.trim(), email, origem: 'externo', ordem: l.length + 1, d4sign_act: '1',
        qualificacao: qualif.trim() || undefined,
      }]);
      setExtNome(''); setExtEmail(''); setQualif('');
    }
  }

  function mudarQualificacao(email: string, v: string) {
    tocado.current = true;
    setLista(l => l.map(s => s.email === email
      ? { ...s, qualificacao: v.slice(0, 60) || undefined } : s));
  }

  function remover(email: string) {
    tocado.current = true;
    setLista(l => l.filter(s => s.email !== email).map((s, i) => ({ ...s, ordem: i + 1 })));
  }

  // Quem assina como "Testemunha" entra na D4 Sign COMO testemunha (act 5),
  // não como parte — é o que o Lucas pediu para os contratos, e vale para
  // todo módulo: a qualificação é a intenção, o act é só a tradução técnica.
  // Derivado no envio (um lugar só) para valer também para pré-selecionados e
  // para quem corrigiu a qualificação na própria linha. Desde 01/09/2026 a
  // testemunha ganha linha própria no documento (seção "Testemunhas:") e
  // carimbo sobre ela — exceto no documento de VENDA, cujo template só tem as
  // duas colunas de parte.
  const ehTestemunha = (q?: string) => String(q || '').trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '') === 'testemunha';

  // Papéis que faltam: o servidor recusa o envio sem eles (documento de venda
  // enviado só com a nossa assinatura é concluído pela D4 Sign e vira "contrato
  // assinado" no ERP — VND-2026-079, 15/09/2026).
  const faltamPapeis = papeisFaltando(lista, papeisObrigatorios);

  async function enviar() {
    if (!lista.length) { message.warning('Adicione ao menos um signatário'); return; }
    if (faltamPapeis.length) {
      message.warning(`Falta quem assina como ${faltamPapeis.join(' e ')} — escolha o papel em "Assina como".`);
      return;
    }
    setEnviando(true);
    try {
      const signatarios = lista.map(s => ({
        ...s, d4sign_act: ehTestemunha(s.qualificacao) ? '5' as const : (s.d4sign_act || '1' as const),
      }));
      const r = await onEnviar({ signatarios, workflow, mensagem: mensagem.trim() || undefined });
      message.success(`Enviado para ${r.signatarios} signatário(s). Os e-mails saem em instantes.`);
      onEnviado(); onClose();
    } catch (e: any) {
      // O backend devolve TODOS os problemas de uma vez, separados por ·
      message.error(e.response?.data?.error || e.message, 10);
    } finally { setEnviando(false); }
  }

  return (
    <Modal
      open={open} onCancel={onClose} title={`Enviar ${titulo || 'documento'} para assinatura`}
      width="94vw" style={{ maxWidth: 900, top: 20 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      okText={<><SendOutlined /> Enviar para assinatura</>}
      onOk={enviar} confirmLoading={enviando}
      okButtonProps={{ disabled: !lista.length || faltamPapeis.length > 0 }}
      cancelText="Cancelar"
    >
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Isto dispara e-mails reais e consome crédito de assinatura"
        description={descricaoDoAviso} />

      {faltamPapeis.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`Falta quem assina como ${faltamPapeis.join(' e ')}`}
          description={'Este documento tem uma linha de assinatura para cada parte. Escolha o papel na coluna "Assina como" — '
            + 'ou adicione a pessoa na faixa abaixo. Enviar sem as duas partes faz a D4 Sign concluir o documento '
            + 'com as assinaturas que houver, e quem ficou de fora nem recebe o e-mail.'} />
      )}

      <Text strong>Quem assina</Text>
      <Table
        size="small" rowKey="email" pagination={false} style={{ marginTop: 8 }}
        locale={{ emptyText: 'Nenhum signatário ainda' }}
        dataSource={lista}
        columns={[
          ...(workflow ? [{ title: '#', dataIndex: 'ordem', key: 'o', width: 45 }] : []),
          { title: 'Nome', dataIndex: 'nome', key: 'n' },
          { title: 'E-mail', dataIndex: 'email', key: 'e' },
          {
            // Editável na própria linha: a qualificação costuma ser lembrada
            // DEPOIS de montar a lista, e obrigar a remover e re-adicionar a
            // pessoa só para corrigir uma palavra seria hostil.
            title: 'Assina como', dataIndex: 'qualificacao', key: 'q', width: 190,
            render: (v: string | undefined, s: SignatarioEnvio) => (
              <AutoComplete
                size="small" style={{ width: '100%' }} value={v || ''}
                placeholder="opcional" allowClear
                options={QUALIFICACOES.map(q => ({ value: q }))}
                filterOption={(i, o) => String(o?.value || '').toLowerCase().includes(i.toLowerCase())}
                onChange={(t) => mudarQualificacao(s.email, t || '')} />
            ),
          },
          {
            title: 'Tipo', dataIndex: 'origem', key: 't', width: 120,
            render: (v: string, s: SignatarioEnvio) => (
              <Space size={4}>
                <Tag color={v === 'interno' ? 'blue' : 'default'}>{v}</Tag>
                {ehTestemunha(s.qualificacao) && <Tag color="purple">testemunha</Tag>}
              </Space>
            ),
          },
          {
            title: '', key: 'x', width: 50,
            render: (_: any, s: SignatarioEnvio) => (
              <Button type="text" danger size="small" icon={<DeleteOutlined />}
                onClick={() => remover(s.email)} />
            ),
          },
        ]}
      />

      {lista.some(s => ehTestemunha(s.qualificacao)) && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
          Testemunha assina na D4 Sign como testemunha, com linha própria na seção "Testemunhas:" do documento (no documento de venda, sem linha).
        </Text>
      )}

      <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 6 }}>
        <Space wrap>
          <Select value={tipo} onChange={(v) => setTipo(v)} style={{ width: 130 }}
            options={[
              { value: 'interno', label: 'Da equipe' },
              { value: 'externo', label: 'De fora' },
            ]} />
          {tipo === 'interno' ? (
            <Select showSearch style={{ width: 320 }} placeholder="Escolha a pessoa"
              value={interno} onChange={setInterno}
              filterOption={(i, o) => String(o?.label || '').toLowerCase().includes(i.toLowerCase())}
              options={usuarios.map(u => ({
                value: u.email, label: u.nome ? `${u.nome} (${u.email})` : u.email,
              }))} />
          ) : (
            <>
              <Input placeholder="Nome" value={extNome} style={{ width: 170 }}
                onChange={(e) => setExtNome(e.target.value)} />
              <Input placeholder="e-mail" value={extEmail} style={{ width: 210 }}
                onChange={(e) => setExtEmail(e.target.value)} />
            </>
          )}
          {/* Corta em 60 aqui também: o banco é VARCHAR(60) e o handler recusa
              acima disso. Sem o corte, o texto longo entra na lista, parece
              aceito, e só é barrado no clique final de envio — depois de montar
              a lista inteira, com a mensagem apontando "Signatário 1" e o texto
              ainda íntegro na tela. */}
          <AutoComplete style={{ width: 190 }} value={qualif}
            onChange={(v) => setQualif((v || '').slice(0, 60))}
            placeholder="Assina como… (opcional)"
            options={QUALIFICACOES.map(q => ({ value: q }))}
            filterOption={(i, o) => String(o?.value || '').toLowerCase().includes(i.toLowerCase())} />
          <Button icon={<PlusOutlined />} onClick={adicionar}>Adicionar</Button>
        </Space>
      </div>

      <Flex vertical gap={6} style={{ marginTop: 14 }}>
        <Checkbox checked={workflow} onChange={(e) => setWorkflow(e.target.checked)}>
          Assinar em ordem (um de cada vez, na sequência da lista)
        </Checkbox>
        {workflow && (
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 24 }}>
            Cada pessoa só recebe o convite depois que a anterior assinar.
          </Text>
        )}
        <div style={{ marginTop: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Recado no e-mail (opcional)</Text>
          <Input.TextArea rows={2} value={mensagem} maxLength={500}
            onChange={(e) => setMensagem(e.target.value)}
            placeholder="Ex.: Segue o documento para assinatura. Qualquer dúvida, me chame." />
        </div>
      </Flex>

      <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 12, marginBottom: 0 }}>
        Depois de enviado, o acompanhamento aparece no próprio documento — o sistema
        verifica sozinho, sem precisar deixar a tela aberta.
      </Paragraph>
    </Modal>
  );
}
